import {
  Box,
  Button,
  HStack,
  VStack,
  Text,
  Select,
  Input,
  Link,
  useToast,
  Collapse
} from "@chakra-ui/react";
import * as XLSX from "xlsx";
import DealLink from "../deal_link";
import { saveAs } from "file-saver";
import React, { useState, useMemo, memo, useCallback, useEffect, useRef } from "react";
import { observer } from "mobx-react-lite";
import { useStores } from "../../store/store_context";
import { FixedSizeList as List } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import DateFilters from "./date_filters";
import RowDetailModal from './row_detail_modal'; // Импортируем наше окно
import { useDisclosure } from '@chakra-ui/react';

const TableData = observer(({ data, renderButtons }) => {
  const { pageStore } = useStores();
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: null,
  });
  const toast = useToast();

  const columns = data.length > 0 ? Object.keys(data[0]) : [];

  // Функция для рендеринга ячейки с учетом типа содержимого
  const renderCellContent = (column, value, item) => {
    if ((column.key == "DocNum" || column.key == "DocPresent") && (item.DocType == "Сделка") && value) {
      // Раньше здесь href присваивался до своего `let` — строки со сделкой
      // падали с ReferenceError при отрисовке.
      return <DealLink id={item["IdDeal"]}>{value}</DealLink>;
    }

    return formatCellValue(value);
  };

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: "desc" };
    });
  };

  const nestedData = useMemo(() => {
    if (!data) return [];

    const groups = {};

    data.forEach((item) => {
      const num = item.Num || "no-num";
      if (!groups[num]) {
        groups[num] = { parent: null, children: [], num: num };
      }

      if (item.DocType === "ЗаказКлиента") {
        groups[num].parent = item;
      } else {
        groups[num].children.push(item);
      }
    });

    // Превращаем в массив. 
    // Если "Заказа клиента" нет, берем первый попавшийся док как временного родителя, 
    // чтобы данные не пропали (опционально)
    return Object.values(groups).map(group => {
      if (!group.parent && group.children.length > 0) {
        return {}
      }
      return group;
    }).filter(g => g.parent); // Оставляем только те, где есть хотя бы один документ
  }, [data]);

  const getMonday = (d) => {
    const date = new Date(d);
    const day = date.getDay();
    const diff = date.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(date.getFullYear(), date.getMonth(), diff);
  };

  const INTERNAL_PARTNERS = [
    "ТД АЛЬЯНС ФОРЕСТ ООО",
    "АЛЬЯНС ФОРЕСТ СПБ ООО",
    "АРТИГЛИО ООО",
    "ЛОДЖИСТИК ИНДАСТРИ ООО"
  ];

  const EXCLUDED_ROLES = [
    "Руководитель отдела продаж",
    "Руководитель удалённого отдела продаж"
  ];

  // Чистая функция для получения диапазона дат
  const getDateRange = (filterType, toISODateString, customStart, customEnd, getMonday) => {
    if (!filterType || filterType === "none" || filterType === "fill" || filterType === "empty") {
      return { start: null, end: null };
    }

    if (filterType === "custom") {
      return { start: customStart, end: customEnd };
    }

    const currentDate = new Date();
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    switch (filterType) {
      case "week": {
        const monday = getMonday(currentDate);
        return {
          start: toISODateString(monday),
          end: toISODateString(new Date(monday.getTime() + 6 * 24 * 60 * 60 * 1000))
        };
      }
      case "month":
        return {
          start: toISODateString(new Date(year, month, 1)),
          end: toISODateString(new Date(year, month + 1, 0))
        };
      case "pastMonth":
        return {
          start: toISODateString(new Date(year, month - 1, 1)),
          end: toISODateString(new Date(year, month, 0))
        };
      case "quarter": {
        const quarter = Math.floor(month / 3);
        return {
          start: toISODateString(new Date(year, quarter * 3, 1)),
          end: toISODateString(new Date(year, quarter * 3 + 3, 0))
        };
      }
      case "halfYear": {
        const half = Math.floor(month / 6);
        return {
          start: toISODateString(new Date(year, half * 6, 1)),
          end: toISODateString(new Date(year, half * 6 + 6, 0))
        };
      }
      case "year":
        return {
          start: toISODateString(new Date(year, 0, 1)),
          end: toISODateString(new Date(year, 11, 31))
        };
      case "pastYear":
        return {
          start: toISODateString(new Date(year - 1, 0, 1)),
          end: toISODateString(new Date(year - 1, 11, 31))
        }
        break;
      default:
        return { start: null, end: null };
    }
  };

  const sortedNestedData = useMemo(() => {
    // 1. Получаем диапазоны дат (ваш существующий код)
    const openDateRange = getDateRange(
      pageStore.order_type_date_start_filter,
      pageStore.toISODateString,
      pageStore.order_start_open_date_filter,
      pageStore.order_end_open_date_filter,
      getMonday
    );

    const closeDateRange = getDateRange(
      pageStore.order_type_date_end_filter,
      pageStore.toISODateString,
      pageStore.order_start_close_date_filter,
      pageStore.order_end_close_date_filter,
      getMonday
    );

    // 2. Фильтрация ПО РОДИТЕЛЮ
    let filtered = nestedData.filter((orderObj) => {
      const item = orderObj.parent; // Применяем все фильтры к родительской строке

      // Текстовые фильтры
      if (pageStore.order_num_filter && !item.Num?.toLowerCase().includes(pageStore.order_num_filter.toLowerCase())) return false;
      if (pageStore.order_deal_filter && !item.IdDeal?.includes(pageStore.order_deal_filter)) return false;

      // Логические фильтры
      if (pageStore.workers && !item.active) return false;

      const isPartnerInternal = INTERNAL_PARTNERS.includes(item.Partner);
      if (pageStore.isSelf !== isPartnerInternal) return false; // Изящная замена if-else

      if (pageStore.selected_view_pdz === "departament-withoutROP" && EXCLUDED_ROLES.includes(item.role_name)) return false;

      // Фильтр даты открытия
      const openType = pageStore.order_type_date_start_filter;
      if (openType === "fill" && !item.AccOpenDate) return false;
      if (openType === "empty" && item.AccOpenDate) return false;
      if (openDateRange.start && openDateRange.end) {
        if (!item.AccOpenDate || item.AccOpenDate < openDateRange.start || item.AccOpenDate > openDateRange.end) return false;
      }

      // Фильтр даты закрытия
      const closeType = pageStore.order_type_date_end_filter;
      if (closeType === "fill" && !item.ClosDate) return false;
      if (closeType === "empty" && item.ClosDate) return false;
      if (closeDateRange.start && closeDateRange.end) {
        if (!item.ClosDate || item.ClosDate < closeDateRange.start || item.ClosDate > closeDateRange.end) return false;
      }

      return true; // Если родитель прошел, весь orderObj (с детьми) попадает в результат
    });

    // 3. Сортировка ПО РОДИТЕЛЮ
    if (sortConfig.key) {
      const isAsc = sortConfig.direction === "asc";
      filtered.sort((a, b) => {
        const valA = a.parent[sortConfig.key]; // Сортируем по полям родителя
        const valB = b.parent[sortConfig.key];

        if (valA == null && valB == null) return 0;
        if (valA == null) return 1;
        if (valB == null) return -1;

        // Числа, Даты, Строки (ваш существующий код сортировки)
        if (typeof valA === "number" && typeof valB === "number") {
          return isAsc ? valA - valB : valB - valA;
        }
        // ... и так далее
      });
    }

    return filtered;
  }, [
    nestedData,
    sortConfig,
    pageStore.order_num_filter,
    pageStore.order_deal_filter,
    pageStore.workers,
    pageStore.isSelf,
    pageStore.selected_view_pdz,
    pageStore.order_type_date_start_filter,
    pageStore.order_start_open_date_filter,
    pageStore.order_end_open_date_filter,
    pageStore.order_type_date_end_filter,
    pageStore.order_start_close_date_filter,
    pageStore.order_end_close_date_filter,
  ]);

  const availableColumns = pageStore.columnConfigPDZ.filter(
    (col) => data.length > 0 && (data[0].hasOwnProperty(col.key) || col.key === "Total") && col.active
  );

  const handleExportToXLSX = async () => {
    var can = await pageStore.CheckPermission("export excel")
    if (!can) {
      toast({
        title: "У вас нет права экспорта в excel",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return
    }
    return
  };

  const formatCellValue = (value) => {
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [year, month, day] = value.split("-");
      return `${day}.${month}.${year}`;
    }
    if (typeof value === "number") {
      return value.toLocaleString("ru-RU");
    }
    return value || "";
  };

  const formatCellValueExcel = (value) => {
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [year, month, day] = value.split("-");
      return `${day}.${month}.${year}`;
    }
    return value || "";
  };

  const [groupBy, setGroupBy] = useState("manager");
  useEffect(() => {
    const view = pageStore.selected_view_pdz;

    // Маппинг: какой вид соответствует какому ключу группировки
    if (view === "manager") setGroupBy("manager");
    else if (view === "org") setGroupBy("org");
    else if (view === "departament" || view === "departament-withoutROP") setGroupBy("departament");
    else if (view === "all") setGroupBy("none");
    // "none" или другое значение, которое ваша функция groupData поймет как отсутствие групп
  }, [pageStore.selected_view_pdz]);
  const [expandedGroups, setExpandedGroups] = useState({});

  const groupData = (data, groupBy, sortConfig = null) => {
    // Если данных нет, возвращаем пустой массив
    if (!data || data.length === 0) return [];

    const grouped = data.reduce((acc, item) => {
      let key;
      // ВАЖНО: берем свойства из item.parent, так как item теперь - это объект заказа
      const parent = item.parent;

      if (pageStore.selected_view_pdz === "all") {
        key = "Все данные"; // Создаем одну общую группу для режима без группировки
      } else if (pageStore.selected_view_pdz === "manager") {
        key = parent.Manager;
      } else if (pageStore.selected_view_pdz === "org") {
        key = parent.Org;
      } else if (pageStore.selected_view_pdz === "departament" || pageStore.selected_view_pdz === "departament-withoutROP") {
        key = parent.departament_name;
      } else if (groupBy === "month") {
        const date = new Date(parent.DateDoc);
        key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
      } else if (groupBy === "year") {
        const date = new Date(parent.DateDoc);
        key = date.getFullYear();
      }

      // Если ключ не определился (например, пустой менеджер), называем его "Не указано"
      if (!key) key = "Прочее / Не указано";

      if (!acc[key]) {
        acc[key] = {
          key: key,
          items: [],
          totalSum: 0,
          paidRubSum: 0,
          deptRubSum: 0,
        };
      }

      acc[key].items.push(item);

      // Суммируем деньги из родителя и всех его детей
      const allDocs = [item.parent, ...(item.children || [])];
      allDocs.forEach(doc => {
        acc[key].totalSum += doc.RevRub || 0;
        acc[key].paidRubSum += doc.PaidRub || 0;
        acc[key].deptRubSum += doc.DeptRub || 0;
      });

      return acc;
    }, {});

    let groupsArray = Object.values(grouped).map(group => ({
      ...group,
      percentPayment: group.totalSum ? (group.paidRubSum / group.totalSum) * 100 : 0,
      percentShipment: group.totalSum ? (group.deptRubSum / group.totalSum) * 100 : 0,
    }));


    if (sortConfig && sortConfig.key) {
      groupsArray.sort((a, b) => {
        let valueA, valueB;

        valueA = a.key;
        valueB = b.key;

        const isAsc = sortConfig.direction === "asc";
        if (typeof valueA === "number" && typeof valueB === "number") {
          return isAsc ? valueA - valueB : valueB - valueA;
        }
        return isAsc
          ? String(valueA || "").localeCompare(String(valueB || ""))
          : String(valueB || "").localeCompare(String(valueA || ""));
      });
    }

    if (sortConfig && sortConfig.key) {
      const isAsc = sortConfig.direction === "asc";

      const compare = (a, b) => {
        if (typeof a === "number" && typeof b === "number") {
          return isAsc ? a - b : b - a;
        }

        return isAsc
          ? String(a ?? "").localeCompare(String(b ?? ""))
          : String(b ?? "").localeCompare(String(a ?? ""));
      };

      groupsArray.forEach(group => {
        // 1️⃣ сортировка items по parent
        group.items?.sort((itemA, itemB) => {
          const valueA = itemA?.parent?.[sortConfig.key];
          const valueB = itemB?.parent?.[sortConfig.key];

          return compare(valueA, valueB);
        });

        // 2️⃣ сортировка children внутри каждого item
        group.items?.forEach(item => {
          item.children?.sort((childA, childB) => {
            const valueA = childA?.[sortConfig.key];
            const valueB = childB?.[sortConfig.key];

            return compare(valueA, valueB);
          });
        });
      });
    }

    return groupsArray;
  };

  const toggleGroup = (key) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const groupedData = groupBy ? groupData(sortedNestedData, groupBy, sortConfig) : null;

  const user = pageStore.all_managers?.find(
    (item) => item?.email == pageStore.email
  );

  // Рассчитываем общие итоги
  const grandTotals = useMemo(() => {
    const totals = {
      RevRub: 0,
      PaidRub: 0,
      DeptRub: 0,
      Count: 0, // Общее количество документов (родители + дети)
      OrdersCount: 0 // Количество уникальных заказов (родителей)
    };

    sortedNestedData.forEach((orderObj) => {
      // 1. Учитываем родителя
      const p = orderObj.parent;
      totals.RevRub += p.RevRub || 0;
      totals.PaidRub += p.PaidRub || 0;
      totals.DeptRub += p.DeptRub || 0;
      totals.Count += 1;
      totals.OrdersCount += 1;

      // 2. Учитываем всех детей
      orderObj.children.forEach((child) => {
        totals.RevRub += child.RevRub || 0;
        totals.PaidRub += child.PaidRub || 0;
        totals.DeptRub += child.DeptRub || 0;
        totals.Count += 1;
      });
    });

    return totals;
  }, [sortedNestedData]);

  const grandPercentPayment = grandTotals.RevRub ? (grandTotals.PaidRub / grandTotals.RevRub) * 100 : 0;
  const grandPercentShipment = grandTotals.RevRub ? (grandTotals.DeptRub / grandTotals.RevRub) * 100 : 0;

  // Формируем плоский список данных для виртуализированного списка
  const [expandedOrders, setExpandedOrders] = useState({});

  // Функция переключения
  const toggleOrder = useCallback((orderNum) => {
    setExpandedOrders((prev) => ({
      ...prev,
      [orderNum]: !prev[orderNum], // инвертируем значение
    }));
  }, []);

  const flatData = useMemo(() => {
    const result = [];

    // 1. Итого (добавляем всегда)
    result.push({
      type: 'grand-total',
      totals: grandTotals,
      percentPayment: grandPercentPayment,
      percentShipment: grandPercentShipment,
    });

    // Защита: если groupedData еще не загрузился или пуст, возвращаем только "Итого"
    if (!groupedData || !Array.isArray(groupedData) || groupedData.length === 0) {
      return result; // <--- ЕСЛИ ВЫ ВИДИТЕ ТОЛЬКО "ИТОГО", КОД ПРЕРЫВАЕТСЯ ЗДЕСЬ
    }

    groupedData.forEach(group => {
      // 2. Заголовок группы (добавляется всегда, если есть данные)
      result.push({ type: 'group-header', groupKey: group.key, group });

      // 3. Заказы (если группа развернута)
      if (expandedGroups && expandedGroups[group.key]) {
        // Защита от отсутствия items
        const items = group.items || [];

        items.forEach(orderObj => {
          // Защита: если у заказа вдруг нет свойства children
          const children = orderObj.children || [];

          // Родительская строка (Заказ клиента)
          result.push({
            type: 'order-parent',
            item: orderObj.parent,
            hasChildren: children.length > 0,
            isExpanded: !!(expandedOrders && expandedOrders[orderObj.num]),
            orderNum: orderObj.num,
            groupKey: group.key,
            children: orderObj.children || [],
            parent: orderObj.parent
          });

          // 4. Дочерние строки (если заказ развернут)
          if (expandedOrders && expandedOrders[orderObj.num]) {
            children.forEach(childItem => {
              result.push({
                type: 'order-child',
                item: childItem,
                orderNum: orderObj.num,
              });
            });
          }
        });
      }
    });

    return result;
  }, [
    groupedData,
    expandedGroups,
    expandedOrders,
    grandTotals,
    grandPercentPayment,
    grandPercentShipment // Добавили недостающие зависимости и убрали дубликаты
  ]);

  // Выносим этот компонент за пределы основного, чтобы он не пересоздавался
  const TableCell = ({ column, children, isBold, isItalic, align = "left", extraProps = {} }) => (
    <Box
      width={column.width}
      minWidth={column.width}
      maxWidth={column.width}
      overflow="hidden"
      textOverflow="ellipsis"
      textAlign={align}
      flexShrink={0}
      px={2}
      py={pageStore.rowHeight}
      fontWeight={isBold ? "bold" : "normal"}
      fontStyle={isItalic ? "italic" : "normal"}
      {...extraProps}
    >
      {children}
    </Box>
  );

  // Колонка для группы (она статичная, поэтому тоже выносим)
  const GroupColumnCell = ({ children, isHeader = false }) => (
    <Box
      width={groupCellWidth} // Ровно 200px (ваша константа)
      minWidth={groupCellWidth}
      maxWidth={groupCellWidth}
      flexShrink={0}
      overflow="hidden"
      textOverflow="ellipsis"
      textAlign="left"
      // Не добавляйте сюда горизонтальный padding, если он уже есть в RowWrapper
      display="flex"
      alignItems="center"
    >
      {children}
    </Box>
  );


  const getGrandTotalValue = (key, item) => {
    if (key === "Num") return item.totals.Count;
    if (key === "PercentPayment") return `${item.percentPayment.toFixed(2)}%`;
    if (key === "PercentShipment") return `${item.percentShipment.toFixed(2)}%`;
    if (["RevRub", "PaidRub", "DeptRub", "Total"].includes(key)) {
      const value = key === "Total" ? item.totals.RevRub : item.totals[key];
      return formatCellValue(value);
    }
    return null;
  };

  const getGroupHeaderValue = (key, group) => {
    if (key === "PercentPayment") return `${group.percentPayment.toFixed(2)}%`;
    if (key === "PercentShipment") return `${group.percentShipment.toFixed(2)}%`;
    if (["RevRub", "PaidRub", "DeptRub", "Total"].includes(key)) {
      const mapKey = {
        RevRub: "totalSum",
        Total: "totalSum",
        PaidRub: "paidRubSum",
        DeptRub: "deptRubSum"
      };
      return formatCellValue(group[mapKey[key]]);
    }
    return null;
  };

  // Где-то перед рендером
  const groupCellWidth = 200;
  const totalTableWidth = availableColumns.reduce((sum, col) => sum + col.width, groupCellWidth);

  const RowRenderer = memo(({ index, style, data }) => {
    const {
      flatData,
      availableColumns,
      toggleOrder,
      toggleGroup,
      expandedGroups,
      onRowClick,
      renderCellContent,
      formatCellValue,
      pageStore
    } = data;

    const row = flatData[index];
    const isGroupedView = true;
    const clickTimeoutRef = useRef(null);

    // --- Более контрастные цвета ---
    const getRowAppearance = () => {
      switch (row.type) {
        case 'group-header':
          return {
            bg: "#0F172A",
            hover: "#1E293B",
            border: "3px solid #ffffff"
          };

        case 'order-parent':
          return {
            bg: "#1E293B",
            hover: "#334155",
            border: "3px solid #ffffff"
          };

        case 'order-child':
          return {
            bg: "#374151",
            hover: "#4B5563",
            border: "2px solid #ffffff"
          };

        case 'grand-total':
          return {
            bg: "#152c6d",
            hover: "#1E3A8A",
            border: "3px solid #ffffff"
          };

        default:
          const isEven = index % 2 === 0;
          return {
            bg: isEven ? "#2E3440" : "#1F2937",
            hover: "#475569",
            border: "2px solid #ffffff"
          };
      }
    };

    const appearance = getRowAppearance();

    const handleClicks = useCallback((item, orderNum, hasChildren) => {
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
        clickTimeoutRef.current = null;
        onRowClick(item);
      } else {
        clickTimeoutRef.current = setTimeout(() => {
          clickTimeoutRef.current = null;
          if (hasChildren && orderNum) {
            toggleOrder(orderNum);
          }
        }, 250);
      }
    }, [onRowClick, toggleOrder]);

    React.useEffect(() => {
      return () => {
        if (clickTimeoutRef.current) clearTimeout(clickTimeoutRef.current);
      };
    }, []);

    const RowWrapper = ({ children, onClick, currentStyle, typeAppearance }) => (
      <Box
        display="flex"
        style={{
          ...currentStyle,
          width: totalTableWidth, // Возвращаем фиксированную ширину
          minWidth: totalTableWidth,
        }}
        bg={typeAppearance.bg}
        _hover={{ bg: typeAppearance.hover }}
        borderBottom={typeAppearance.border}
        alignItems="center"
        cursor="pointer"
        color="white"
        px="14px" // <--- Запомним это значение
        py={pageStore.rowHeight}
        transition="background 0.2s ease"
        onClick={onClick}
      >
        {children}
      </Box>
    );


    // --- ORDER PARENT / CHILD ---
    if (row.type === 'order-parent' || row.type === 'order-child') {
      const isParent = row.type === 'order-parent';

      return (
        <RowWrapper
          currentStyle={style}
          typeAppearance={appearance}
          onClick={() => handleClicks(
            row,
            isParent ? row.orderNum : null,
            isParent && row.hasChildren
          )}
        >
          {isGroupedView && (
            <GroupColumnCell>
              {isParent ? (
                <HStack spacing={3}>
                  {row.hasChildren && (
                    <Text
                      fontSize="2xl"
                      fontWeight="bold"
                      color="orange.300"
                      width="28px"
                      textAlign="center"
                    >
                      {row.isExpanded ? "▼" : "▶"}
                    </Text>
                  )}
                  {!row.hasChildren && <Box width="28px" py={pageStore.rowHeight} />}
                  <Text
                    fontSize="2xl"
                    color="gray.200"
                    fontWeight="bold"
                    isTruncated
                  >
                    {row.groupKey}
                  </Text>
                </HStack>
              ) : ""}
            </GroupColumnCell>
          )}

          {availableColumns.map(col => (
            <TableCell
              key={col.key}
              column={col}
              flexShrink={0}
              extraProps={{
                color: isParent ? "white" : "gray.300",
                fontSize: isParent ? "2xl" : "xl"
              }}
            >
              {renderCellContent(col, row.item[col.key], row.item)}
            </TableCell>
          ))}
        </RowWrapper>
      );
    }

    // --- GRAND TOTAL ---
    if (row.type === 'grand-total') {
      return (
        <RowWrapper currentStyle={style} typeAppearance={appearance}>
          {isGroupedView && (
            <GroupColumnCell>
              <Text fontWeight="bold" fontSize="3xl" color="yellow.300">
                ИТОГО
              </Text>
            </GroupColumnCell>
          )}

          {availableColumns.map((column) => {
            const val = getGrandTotalValue(column.key, row);
            return (
              <TableCell
                key={column.key}
                column={column}
                isBold={!!val}
                flexShrink={0}
                extraProps={{
                  color: "yellow.300",
                  fontSize: "xl"
                }}
              >
                {val}
              </TableCell>
            );
          })}
        </RowWrapper>
      );
    }

    // --- GROUP HEADER ---
    if (row.type === 'group-header') {
      return (
        <RowWrapper
          currentStyle={style}
          typeAppearance={appearance}
          onClick={() => toggleGroup(row.groupKey)}
        >
          <GroupColumnCell>
            <HStack spacing={3}>
              <Button
                colorScheme="orange"
                size="sm"
                variant="solid"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleGroup(row.groupKey);
                }}
              >
                {expandedGroups[row.groupKey] ? "-" : "+"}
              </Button>

              <Text
                fontWeight="bold"
                fontSize="3xl"
                color="orange.300"
              >
                {row.groupKey}
              </Text>
            </HStack>
          </GroupColumnCell>

          {availableColumns.map((column) => {
            const val = getGroupHeaderValue(column.key, row.group);
            return (
              <TableCell
                key={column.key}
                column={column}
                isBold={!!val}
                flexShrink={0}
                extraProps={{
                  color: "orange.200",
                  fontSize: "xl"
                }}
              >
                {val}
              </TableCell>
            );
          })}
        </RowWrapper>
      );
    }

    // --- DEFAULT ROW ---
    return (
      <RowWrapper currentStyle={style} typeAppearance={appearance}>
        {isGroupedView && (
          <GroupColumnCell>
            <Text fontSize="lg" color="gray.400">
              {row.isFirst ? row.groupKey : ""}
            </Text>
          </GroupColumnCell>
        )}

        {availableColumns.map((column) => (
          <TableCell
            key={column.key}
            column={column}
            flexShrink={0}
            isItalic={!row.item.active}
            extraProps={{ fontSize: "lg" }}
          >
            {column.key === "Total" && isGroupedView && row.isLast
              ? formatCellValue(row.groupTotal)
              : renderCellContent(column, row.item[column.key], row.item)
            }
          </TableCell>
        ))}
      </RowWrapper>
    );
  });

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedItem, setSelectedItem] = React.useState(null);

  const handleRowClick = (item) => {
    setSelectedItem(item);
    onOpen();
  };

  const buttons = (
    <>
      <Button
        onClick={handleExportToXLSX}
        bg="#333"
        color="white"
        width={"15vw"}
        _hover={{
          background: "#444",
        }}
        mr={3}
      >
        Экспорт в Excel
      </Button>
      <Button
        onClick={() => {
          // Развернуть все группы
          if (groupedData) {
            const allExpanded = {};
            groupedData.forEach((group) => {
              allExpanded[group.key] = true;
            });
            setExpandedGroups(allExpanded);
          }
        }}
        bg="#333"
        color="white"
        width={"15vw"}
        _hover={{
          background: "#444",
        }}
        mr={3}
      >
        Развернуть всё
      </Button>
      <Button
        onClick={() => {
          // Свернуть все группы
          setExpandedGroups({});
        }}
        bg="#333"
        color="white"
        width={"15vw"}
        _hover={{
          background: "#444",
        }}
      >
        Свернуть всё
      </Button>
    </>
  )

  // Внутри вашего основного компонента:

  if (!pageStore?.data_pdz?.length) return null;

  // Собираем пропсы для передачи в виртуализованный список
  const itemData = {
    flatData,
    availableColumns,
    pageStore,
    expandedGroups,
    expandedOrders,
    toggleGroup,
    toggleOrder, // функция для раскрытия "Заказа"
    onRowClick: handleRowClick, // ПЕРЕДАЕМ СЮДА
    renderCellContent,
    formatCellValue
  };

  return (
    <>
      <Collapse in={pageStore.open_filter} animateOpacity>
        {renderButtons ? renderButtons(buttons) : buttons}
      </Collapse>

      <VStack alignItems="flex-start" spacing={4} w="100%">
        <DateFilters />

        {/* 1. Главный контейнер, который отвечает за ЕДИНУЮ горизонтальную прокрутку */}
        <Box
          w="100%"
          maxW="100vw"
          overflowX="auto" // Горизонтальный скролл живет здесь
          overflowY="hidden"
          borderRadius="md"
          borderWidth="1px"
          borderColor="gray.300"
        >
          {/* 2. Обертка, которая принудительно растягивает родителя под ширину таблицы */}
          <Box width={totalTableWidth} minWidth="max-content">

            {/* Липкая шапка таблицы — теперь она часть общего широкого контейнера */}
            {/* Липкая шапка таблицы */}
            <Box
              display="flex"
              bg="black"
              color="white"
              // Убираем p="10px" и ставим такие же отступы, как в RowWrapper
              px="14px"
              py="10px"
              position="sticky"
              top={0}
              zIndex={10}
              width={totalTableWidth} // Шапка должна быть такой же ширины
            >
              <GroupColumnCell isHeader>Группа</GroupColumnCell>

              {availableColumns.map((column) => (
                <Box
                  key={column.key}
                  flexShrink={0}
                  width={column.width}
                  minWidth={column.width}
                  maxWidth={column.width}
                  overflow="hidden"
                  textOverflow="ellipsis"
                  cursor="pointer"
                  px={2}
                  py={pageStore.rowHeight}
                  onClick={() => handleSort(column.key)}
                >
                  <HStack>
                    <Text fontSize="xl">{column.title}</Text>
                    {sortConfig.key === column.key && (
                      <Text fontSize="xl">{sortConfig.direction === "asc" ? "↑" : "↓"}</Text>
                    )}
                  </HStack>
                </Box>
              ))}
            </Box>

            {/* Виртуализованный список */}
            <Box height="100vh">
              <AutoSizer disableWidth>
                {/* disableWidth говорит AutoSizer не пытаться считать ширину самому */}
                {({ height }) => (
                  <List
                    height={height}
                    itemCount={flatData.length}
                    itemSize={160}
                    /* 3. КЛЮЧЕВОЙ МОМЕНТ: 
                       Передаем полную ширину. List будет думать, что он очень широкий, 
                       и не создаст свой внутренний горизонтальный скроллбар.
                    */
                    width={totalTableWidth}
                    itemData={itemData}
                    style={{ overflowX: 'hidden' }} // Явно запрещаем внутренний скролл
                  >
                    {RowRenderer}
                  </List>
                )}
              </AutoSizer>
            </Box>
          </Box>
        </Box>
      </VStack>

      <RowDetailModal
        isOpen={isOpen}
        onClose={onClose}
        item={selectedItem}
        availableColumns={availableColumns}
      />
    </>
  );
});

export default TableData;